import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import type { ActiveNote, Station } from '@hcc/types';
import { usePerson, useAppendEvent } from '../../hooks/usePeople';
import { useIntentKey } from '../../hooks/useIntentKey';
import { ApiError } from '../../lib/api';
import { Button } from '../ui/Button';
import { NotesPanel } from './NotesPanel';
import { AddNoteModal } from './AddNoteModal';

/**
 * Reusable modal that shows a person's notes and lets staff add /
 * remove them. Wraps NotesPanel + AddNoteModal so each calling
 * surface (reception, station, admin) gets the same UX without
 * duplicating state.
 *
 * `station` auto-tags newly added notes — pass it when opening from
 * a specific desk so other desks can see where the note came from.
 */
export function PersonNotesDialog({
  open,
  personId,
  personName,
  station,
  onClose,
}: {
  open: boolean;
  personId: string;
  personName: string;
  station?: Station;
  onClose: () => void;
}) {
  const personFull = usePerson(open ? personId : null);
  const append = useAppendEvent(personId);
  const noteIntent = useIntentKey();
  const [adding, setAdding] = useState(false);

  if (!open) return null;
  const notes = personFull.data?.state.notes ?? [];

  async function addNote(input: { text: string; station?: Station }) {
    try {
      await append.mutateAsync({
        event_type: 'NoteAdded',
        payload: input,
        client_event_id: noteIntent.key,
      } as Parameters<typeof append.mutateAsync>[0]);
      noteIntent.renew();
      toast.success('Note added');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not add note');
      throw err;
    }
  }

  async function removeNote(note: ActiveNote) {
    try {
      await append.mutateAsync({
        event_type: 'NoteRemoved',
        payload: { note_event_id: note.event_id },
      } as Parameters<typeof append.mutateAsync>[0]);
      toast.success('Note removed');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove note');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="notes-dialog-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-fg/40 p-4 backdrop-blur-sm aurora-fade-in"
    >
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className="flex items-center justify-between gap-2 border-b border-divider/10 px-4 py-3">
          <h2 id="notes-dialog-title" className="truncate text-base font-semibold tracking-tight text-fg">
            Notes — {personName}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<X className="h-4 w-4" />}
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto p-4">
          <NotesPanel notes={notes} onAdd={() => setAdding(true)} onRemove={removeNote} />
        </div>
      </div>
      <AddNoteModal
        open={adding}
        personName={personName}
        station={station}
        onClose={() => setAdding(false)}
        onSubmit={addNote}
      />
    </div>
  );
}
